import React from 'react';
import * as PropTypes from 'prop-types';
import MenuTitle from './MenuTitle';
import { Loading } from '../Loading/Loading';
import { formatDate } from '../../utils/formatter';
import {
  Button,
  Card,
  InputGroup,
  FormControl,
  Form as BSForm,
  ListGroup,
  Pagination,
} from 'react-bootstrap';
import { Formik, Form, Field } from 'formik';
import { faSync } from '@whitebox-learning/fontawesome-pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ConceptNameInput from './ConceptNameInput';
import ConceptListItem from './ConceptListItem';
import WbAlert from '../Common/WbAlert';

const ForkConceptMenu = ({
  isDirty,
  forkConcept,
  loadConcepts,
  back,
  close,
  loading,
  concepts,
  loadingList,
}) => {
  /* Get specifications options from the concepts */
  const specifications = concepts
    .map((c) => ({
      id: c.specificationId,
      name: c.specificationName,
    }))
    .filter((elem, i, arr) => arr.findIndex((s) => s.id === elem.id) === i)
    .sort((a, b) => (a.lastModified < b.lastModified ? 1 : -1));
  const [page, setPage] = React.useState(1);
  const [name, setName] = React.useState(null);
  const [spec, setSpec] = React.useState(null);
  const [shared, setShared] = React.useState(false);
  const [filteredConcepts, setFilteredConcepts] = React.useState(concepts);

  React.useEffect(() => {
    setFilteredConcepts(
      concepts.filter(
        (c) =>
          (!name || c.name.toLowerCase().includes(name.toLowerCase())) &&
          (!spec || c.specificationId === parseInt(spec)) &&
          !!c.isShared === shared
      )
    );
  }, [name, spec, shared]);

  const conceptsPerPage = 8;
  const totalPages = Math.ceil((filteredConcepts.length - 1) / conceptsPerPage);

  const hasSharedConcepts = concepts.some((c) => c.isShared);

  return (
    <div className="fm-simple-panel">
      {loading && <Loading />}
      <MenuTitle
        back={back}
        close={close}
        extraButtons={
          <Button
            variant="secondary"
            onClick={loadConcepts}
            title="Refresh Design List"
          >
            <FontAwesomeIcon icon={faSync} spin={loadingList} />
          </Button>
        }
      >
        Fork
      </MenuTitle>
      <div className="fm-scroll">
        <h4>Fork an Existing Design</h4>

        {isDirty && (
          <WbAlert variant="warning">
            Forking a design will start a new design.{' '}
            <i>Any unsaved changes will be lost.</i>
          </WbAlert>
        )}

        <Formik
          initialValues={{
            selectedConcept: null,
            conceptName: '',
            shared: false,
          }}
          onSubmit={async (values) => {
            const conceptID = values.selectedConcept.revisions[0].id;
            forkConcept(conceptID, values.conceptName);
          }}
        >
          {(props) => (
            <Form>
              <p className="mb-1">
                Forking a design will start a new design with inputs set based
                on the design you are forking.
              </p>
              <p className="mb-1">
                You can choose a design to fork from the list below.
              </p>
              <hr />

              <div className="bg-light pt-1 p-2 mb-2 border border-rounded">
                <span className="fst-italic">Filter designs by:</span>
                <InputGroup className="my-1" size="sm">
                  <InputGroup.Text id="filter-name">
                    Concept Name:
                  </InputGroup.Text>
                  <FormControl
                    placeholder="Any concept name"
                    aria-label="Filter by name"
                    aria-describedby="filter-name"
                    onChange={(e) => setName(e.target.value || null)}
                  />
                </InputGroup>

                <InputGroup className="mb-1" size="sm">
                  <InputGroup.Text id="filter-spec">
                    Specification:
                  </InputGroup.Text>
                  <BSForm.Select
                    size="sm"
                    defaultValue="-- All Specs --"
                    aria-label="Filter by spec"
                    aria-describedby="filter-spec"
                    onChange={(e) => setSpec(e.target.value || null)}
                  >
                    <option value="">-- All Specs --</option>
                    {specifications.map((s) => (
                      <option value={s.id} key={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </BSForm.Select>
                </InputGroup>

                {hasSharedConcepts && (
                  <InputGroup className="mb-1" size="sm">
                    <InputGroup.Text id="filter-shared">Show:</InputGroup.Text>
                    <BSForm.Select
                      size="sm"
                      defaultValue="false"
                      aria-label="Filter by shared"
                      aria-describedby="filter-shared"
                      onChange={(e) => setShared(e.target.value === 'true')}
                    >
                      <option value="false">My Designs</option>
                      <option value="true">Shared Designs</option>
                    </BSForm.Select>
                  </InputGroup>
                )}
              </div>

              {!filteredConcepts.length && (
                <p className="text-muted">No design concepts found.</p>
              )}

              <Field name="selectedConcept">
                {({ form }) => (
                  <>
                    {form.values['selectedConcept'] ? (
                      <>
                        <Card className="mb-2">
                          <Card.Header>You chose:</Card.Header>
                          <Card.Body>
                            <h5>
                              {form.values[
                                'selectedConcept'
                              ].name.toUpperCase()}
                            </h5>
                            <div>
                              <i>Last Modified:</i>{' '}
                              {formatDate(
                                new Date(
                                  form.values['selectedConcept'].lastModified
                                ),
                                'datetime'
                              )}
                            </div>
                            <div>
                              <i>Notes:</i>{' '}
                              {
                                form.values['selectedConcept'].revisions[0]
                                  .notes
                              }
                            </div>
                            <Button
                              type="button"
                              variant="secondary"
                              className="btn-sm mt-3"
                              onClick={() =>
                                form.setFieldValue('selectedConcept', null)
                              }
                            >
                              Choose a different design
                            </Button>
                          </Card.Body>
                        </Card>
                      </>
                    ) : (
                      <>
                        <ListGroup>
                          {filteredConcepts
                            .slice(
                              (page - 1) * conceptsPerPage,
                              page * conceptsPerPage
                            )
                            .map((c) => (
                              <ConceptListItem
                                key={c.name}
                                concept={c}
                                handleClick={(c) =>
                                  form.setFieldValue('selectedConcept', c)
                                }
                              />
                            ))}
                        </ListGroup>
                        {totalPages > 1 && (
                          <Pagination
                            size="sm"
                            className="m-0 my-2 justify-content-end"
                          >
                            {[...Array(totalPages).keys()].map((index) =>
                              ((i) => (
                                <Pagination.Item
                                  key={i}
                                  active={i === page}
                                  onClick={(e) =>
                                    e.target.text &&
                                    setPage(parseInt(e.target.text))
                                  }
                                >
                                  {i}
                                </Pagination.Item>
                              ))(++index)
                            )}
                          </Pagination>
                        )}
                      </>
                    )}
                  </>
                )}
              </Field>
              {props.values['selectedConcept'] && (
                <>
                  <ConceptNameInput existingConcepts={concepts} />
                  <Button type="submit" className="btn-block">
                    Fork design
                  </Button>
                </>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

ForkConceptMenu.propTypes = {
  forkConcept: PropTypes.func.isRequired,
  back: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  concepts: PropTypes.array,
  loadConcepts: PropTypes.func.isRequired,
  loadingList: PropTypes.bool,
};

ForkConceptMenu.defaultProps = {
  concepts: [],
  loading: false,
  loadingList: true,
};

export default ForkConceptMenu;
